import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
    FaSearch, 
    FaPhoneAlt, 
    FaEnvelope, 
    FaWhatsapp, 
    FaClock,
    FaQuestionCircle,
    FaExclamationTriangle,
    FaCheckCircle,
    FaStar,
    FaShippingFast,
    FaCreditCard,
    FaUtensils,
    FaUser,
    FaMapMarkerAlt
} from "react-icons/fa";

const HelpSupportCenter = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [activeSupportOption, setActiveSupportOption] = useState("all");

    // Sample data for support topics
    const supportTopics = [
        {
            id: 1,
            title: "Order Issues",
            icon: <FaShippingFast />,
            description: "Problems with your current or past orders",
            color: "#FF6B6B",
            bgColor: "#FFF5F5",
            subTopics: [
                { title: "Order not delivered", count: 125 },
                { title: "Wrong items received", count: 89 },
                { title: "Late delivery", count: 156 },
                { title: "Order cancellation", count: 67 },
                { title: "Missing items", count: 45 }
            ]
        },
        {
            id: 2,
            title: "Payments & Refunds",
            icon: <FaCreditCard />,
            description: "Payment failures, refunds, and billing issues",
            color: "#4ECDC4",
            bgColor: "#F0FCFB",
            subTopics: [
                { title: "Payment failed", count: 203 },
                { title: "Refund status", count: 178 },
                { title: "Payment method issues", count: 92 },
                { title: "Promo code not working", count: 134 },
                { title: "Charged twice", count: 23 }
            ]
        },
        {
            id: 3,
            title: "Food Quality & Safety",
            icon: <FaUtensils />,
            description: "Food quality, hygiene, and safety concerns",
            color: "#FFD166",
            bgColor: "#FFFCF0",
            subTopics: [
                { title: "Food quality complaint", count: 56 },
                { title: "Hygiene issues", count: 34 },
                { title: "Wrong temperature", count: 45 },
                { title: "Packaging damaged", count: 78 },
                { title: "Allergy concerns", count: 12 }
            ]
        },
        {
            id: 4,
            title: "Account & Profile",
            icon: <FaUser />,
            description: "Login, account settings, and profile management",
            color: "#118AB2",
            bgColor: "#F0F9FF",
            subTopics: [
                { title: "Can't login", count: 145 },
                { title: "Update profile", count: 67 },
                { title: "Delete account", count: 34 },
                { title: "Two-factor authentication", count: 89 },
                { title: "Privacy settings", count: 56 }
            ]
        },
        {
            id: 5,
            title: "Delivery & Address",
            icon: <FaMapMarkerAlt />,
            description: "Delivery locations, addresses, and zones",
            color: "#06D6A0",
            bgColor: "#F0FDF9",
            subTopics: [
                { title: "Change delivery address", count: 167 },
                { title: "Delivery not available", count: 98 },
                { title: "Multiple addresses", count: 45 },
                { title: "Track delivery partner", count: 234 },
                { title: "Delivery instructions", count: 67 }
            ]
        },
        {
            id: 6,
            title: "Restaurant Issues",
            icon: <FaStar />,
            description: "Problems with specific restaurants",
            color: "#9D4EDD",
            bgColor: "#F9F0FF",
            subTopics: [
                { title: "Restaurant closed", count: 45 },
                { title: "Menu incorrect", count: 67 },
                { title: "Partner restaurant complaint", count: 89 },
                { title: "Rating & reviews", count: 156 },
                { title: "Restaurant suggestions", count: 34 }
            ]
        }
    ];

    // Quick solutions
    const quickSolutions = [
        {
            id: 1,
            title: "Instant Refund for Late Delivery",
            description: "Get instant 20% refund if delivery is 30+ minutes late",
            icon: "⏰",
            time: "2 min",
            link: "/support/refund/late-delivery"
        },
        {
            id: 2,
            title: "Re-order Missing Items",
            description: "Get missing items re-delivered for free",
            icon: "🔄",
            time: "5 min",
            link: "/support/reorder/missing"
        },
        {
            id: 3,
            title: "Emergency Contact Restaurant",
            description: "Directly contact restaurant for urgent issues",
            icon: "📞",
            time: "Instant",
            link: "/support/contact/restaurant"
        },
        {
            id: 4,
            title: "Download Invoice",
            description: "Get your order invoice instantly",
            icon: "📄",
            time: "1 min",
            link: "/support/invoice"
        }
    ];

    // Recent orders for context
    const recentOrders = [
        {
            id: "ORD-789456",
            restaurant: "Burger Palace",
            status: "Delivered",
            time: "2 hours ago",
            amount: "₹425",
            issues: ["Late delivery", "Missing fries"]
        },
        {
            id: "ORD-123789",
            restaurant: "Pizza Corner",
            status: "Cancelled",
            time: "1 day ago",
            amount: "₹599",
            issues: ["Payment failed"]
        },
        {
            id: "ORD-456123",
            restaurant: "Chinese Wok",
            status: "Delivered",
            time: "3 days ago",
            amount: "₹750",
            issues: ["Wrong item"]
        }
    ];

    // Support options
    const supportOptions = [
        { id: "call", label: "Call Support", icon: <FaPhoneAlt />, time: "2 min", description: "Speak directly with our executive" },
        { id: "chat", label: "Live Chat", icon: <FaWhatsapp />, time: "Instant", description: "24/7 chat support" },
        { id: "email", label: "Email Support", icon: <FaEnvelope />, time: "24 hours", description: "Detailed issue resolution" },
        { id: "ticket", label: "Raise Ticket", icon: <FaQuestionCircle />, time: "Response in 2 hours", description: "For complex issues" }
    ];

    // Filtered topics based on search
    const filteredTopics = supportTopics.filter(topic => 
        topic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        topic.subTopics.some(sub => sub.title.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const styles = {
        container: {
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "20px",
            paddingTop: window.innerWidth<500 ? "0px" : " ",
            paddingLeft: window.innerWidth<500 ? "0px" : " ",
            paddingRight: window.innerWidth<500 ? "25px" : " ",
            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"
        },
        header: {
            textAlign: "center",
            marginBottom: "00px"
        },
        title: {
            fontSize: "36px",
            fontWeight: "700",
            color: "#1a1a1a",
            marginBottom: "10px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent"
        },
        subtitle: {
            fontSize: "16px",
            color: "#666",
            marginBottom: "30px",
            maxWidth: "600px",
            margin: "0 auto"
        },
        heroSection: {
            backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "20px",
            padding: "40px",
            marginBottom: "40px",
            color: "white",
            textAlign: "center",
            position: "relative",
            overflow: "hidden"
        },
        heroTitle: {
            fontSize: "28px",
            fontWeight: "600",
            marginBottom: "15px"
        },
        heroSubtitle: {
            fontSize: "16px",
            opacity: 0.9,
            marginBottom: "30px"
        },
        searchContainer: {
            position: "relative",
            maxWidth: "600px",
            margin: "0 auto 40px"
        },
        searchInput: {
            width: "100%",
            padding: "18px 60px 18px 30px",
            fontSize: "16px",
            border: "none",
            borderRadius: "15px",
            outline: "none",
            backgroundColor: "white",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            color: "#333"
        },
        searchIcon: {
            position: "absolute",
            right: "25px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#667eea",
            fontSize: "20px"
        },
        topicsGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "25px",
            marginBottom: "40px"
        },
        topicCard: {
            backgroundColor: "#fff",
            borderRadius: "15px",
            padding: "25px",
            cursor: "pointer",
            transition: "all 0.3s ease",
            border: "2px solid transparent",
            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            height: "100%"
        },
        topicCardHover: {
            transform: "translateY(-5px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            borderColor: "#667eea"
        },
        topicHeader: {
            display: "flex",
            alignItems: "center",
            gap: "15px",
            marginBottom: "15px"
        },
        topicIcon: {
            fontSize: "24px",
            padding: "15px",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        },
        topicTitle: {
            fontSize: "18px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "5px"
        },
        topicDescription: {
            fontSize: "14px",
            color: "#666",
            marginBottom: "20px",
            lineHeight: "1.5"
        },
        subTopicsList: {
            marginTop: "auto"
        },
        subTopicItem: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 0",
            borderBottom: "1px solid #f0f0f0",
            fontSize: "14px"
        },
        subTopicTitle: {
            color: "#555",
            fontWeight: "500"
        },
        subTopicCount: {
            backgroundColor: "#f0f0f0",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600"
        },
        quickHelpSection: {
            backgroundColor: "#f8f9ff",
            borderRadius: "20px",
            padding: "30px",
            marginBottom: "40px"
        },
        sectionTitle: {
            fontSize: "24px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "25px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
        },
        quickSolutionsGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px"
        },
        solutionCard: {
            backgroundColor: "white",
            borderRadius: "15px",
            padding: "25px",
            textDecoration: "none",
            display: "block",
            transition: "all 0.3s ease",
            border: "1px solid #e8e8e8",
            cursor: "pointer"
        },
        solutionCardHover: {
            transform: "translateY(-5px)",
            boxShadow: "0 8px 25px rgba(102, 126, 234, 0.15)",
            borderColor: "#667eea"
        },
        solutionIcon: {
            fontSize: "32px",
            marginBottom: "15px"
        },
        solutionTitle: {
            fontSize: "16px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px"
        },
        solutionDescription: {
            fontSize: "14px",
            color: "#666",
            marginBottom: "15px",
            lineHeight: "1.5"
        },
        solutionTime: {
            display: "inline-block",
            backgroundColor: "#e8f4ff",
            color: "#118AB2",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600"
        },
        supportOptionsSection: {
            marginBottom: "40px"
        },
        supportOptionsGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "20px"
        },
        supportOptionCard: {
            backgroundColor: "white",
            borderRadius: "15px",
            padding: "25px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            border: "2px solid transparent",
            boxShadow: "0 4px 15px rgba(0,0,0,0.05)"
        },
        supportOptionCardHover: {
            transform: "translateY(-5px)",
            boxShadow: "0 8px 25px rgba(0,0,0,0.1)"
        },
        supportOptionCardActive: {
            borderColor: "#667eea",
            backgroundColor: "#f8f9ff"
        },
        supportOptionIcon: {
            fontSize: "32px",
            color: "#667eea",
            marginBottom: "15px"
        },
        supportOptionLabel: {
            fontSize: "16px",
            fontWeight: "600",
            color: "#1a1a1a",
            marginBottom: "8px"
        },
        supportOptionDescription: {
            fontSize: "13px",
            color: "#666",
            marginBottom: "10px"
        },
        supportOptionTime: {
            fontSize: "12px",
            color: "#118AB2",
            fontWeight: "600"
        },
        recentOrdersSection: {
            backgroundColor: "#fff",
            borderRadius: "20px",
            padding: "30px",
            marginBottom: "0px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.05)"
        },
        orderCard: {
            backgroundColor: "#f8f9ff",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "15px",
            border: "1px solid #e8e8e8"
        },
        orderHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px"
        },
        orderId: {
            fontSize: "14px",
            fontWeight: "600",
            color: "#667eea"
        },
        orderStatus: {
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "600"
        },
        statusDelivered: {
            backgroundColor: "#d4edda",
            color: "#155724"
        },
        statusCancelled: {
            backgroundColor: "#f8d7da",
            color: "#721c24"
        },
        orderDetails: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
            marginBottom: "15px"
        },
        orderDetailItem: {
            fontSize: "14px",
            color: "#666"
        },
        orderIssues: {
            display: "flex",
            flexWrap: "wrap",
            gap: "8px"
        },
        issueTag: {
            backgroundColor: "#fff5f5",
            color: "#ff6b6b",
            padding: "4px 12px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: "500"
        },
        emptyState: {
            textAlign: "center",
            padding: "60px 20px",
            color: "#999"
        },
        statsSection: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "20px",
            marginBottom: "40px"
        },
        statCard: {
            backgroundColor: "white",
            borderRadius: "15px",
            padding: "25px",
            textAlign: "center",
            boxShadow: "0 4px 15px rgba(0,0,0,0.05)"
        },
        statNumber: {
            fontSize: "32px",
            fontWeight: "700",
            color: "#667eea",
            marginBottom: "10px"
        },
        statLabel: {
            fontSize: "14px",
            color: "#666",
            fontWeight: "500"
        },
        ctaSection: {
            backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "20px",
            padding: "40px",
            textAlign: "center",
            color: "white",
            marginBottom: "40px"
        },
        ctaTitle: {
            fontSize: "24px",
            fontWeight: "600",
            marginBottom: "15px"
        },
        ctaButton: {
            backgroundColor: "white",
            color: "#667eea",
            border: "none",
            padding: "15px 40px",
            borderRadius: "50px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
            marginTop: "20px"
        },
        ctaButtonHover: {
            transform: "translateY(-3px)",
            boxShadow: "0 8px 25px rgba(255,255,255,0.3)"
        }
    };

    // Handle support option click
    const handleSupportOptionClick = (optionId) => {
        setActiveSupportOption(optionId);
        // Redirect or show modal based on option
        switch(optionId) {
            case 'call':
                window.location.href = 'tel:+18001234567';
                break;
            case 'chat':
                window.location.href = '/support/live-chat';
                break;
            case 'email':
                window.location.href = 'mailto:support@foodapp.com';
                break;
            case 'ticket':
                window.location.href = '/support/raise-ticket';
                break;
            default:
                break;
        }
    };

    // Get status style
    const getStatusStyle = (status) => {
        switch(status.toLowerCase()) {
            case 'delivered':
                return { ...styles.orderStatus, ...styles.statusDelivered };
            case 'cancelled':
                return { ...styles.orderStatus, ...styles.statusCancelled };
            default:
                return styles.orderStatus;
        }
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
             
            </div>

            {/* Stats Section */}
            <div style={styles.statsSection}>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>24/7</div>
                    <div style={styles.statLabel}>Support Available</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>98%</div>
                    <div style={styles.statLabel}>Issues Resolved</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}><FaClock /> 15min</div>
                    <div style={styles.statLabel}>Average Response Time</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statNumber}>4.8 <FaStar /></div>
                    <div style={styles.statLabel}>Support Rating</div>
                </div>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer}>
                <input
                    type="text"
                    placeholder="What do you need help with? (e.g., 'late delivery', 'refund', 'missing item')"
                    style={styles.searchInput}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div style={styles.searchIcon}>
                    <FaSearch />
                </div>
            </div>

            {/* Support Options */}
            <div style={styles.supportOptionsSection}>
                <h3 style={styles.sectionTitle}>
                    <FaQuestionCircle /> Quick Support Options
                </h3>
                <div style={styles.supportOptionsGrid}>
                    {supportOptions.map(option => (
                        <div
                            key={option.id}
                            style={{
                                ...styles.supportOptionCard,
                                ...(activeSupportOption === option.id ? styles.supportOptionCardActive : {}),
                                ':hover': styles.supportOptionCardHover
                            }}
                            onClick={() => handleSupportOptionClick(option.id)}
                            onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
                            onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                        >
                            <div style={styles.supportOptionIcon}>{option.icon}</div>
                            <div style={styles.supportOptionLabel}>{option.label}</div>
                            <div style={styles.supportOptionDescription}>{option.description}</div>
                            <div style={styles.supportOptionTime}>⏱️ {option.time}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Topics Grid */}
            <div>
                <h3 style={styles.sectionTitle}>
                    <FaExclamationTriangle /> Browse Help Topics
                </h3>
                {filteredTopics.length === 0 ? (
                    <div style={styles.emptyState}>
                        <p style={{ fontSize: "18px", marginBottom: "10px" }}>No topics found</p>
                        <p>Try a different search term or contact support directly</p>
                    </div>
                ) : (
                    <div style={styles.topicsGrid}>
                        {filteredTopics.map(topic => (
                            <div
                                key={topic.id}
                                style={styles.topicCard}
                                onClick={() => setSelectedTopic(topic)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-5px)";
                                    e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.15)";
                                    e.currentTarget.style.borderColor = topic.color;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.08)";
                                    e.currentTarget.style.borderColor = "transparent";
                                }}
                            >
                                <div style={styles.topicHeader}>
                                    <div style={{
                                        ...styles.topicIcon,
                                        backgroundColor: topic.bgColor,
                                        color: topic.color
                                    }}>
                                        {topic.icon}
                                    </div>
                                    <div>
                                        <h4 style={styles.topicTitle}>{topic.title}</h4>
                                        <p style={styles.topicDescription}>{topic.description}</p>
                                    </div>
                                </div>
                                <div style={styles.subTopicsList}>
                                    {topic.subTopics.slice(0, 4).map((subTopic, index) => (
                                        <div key={index} style={styles.subTopicItem}>
                                            <span style={styles.subTopicTitle}>{subTopic.title}</span>
                                            <span style={styles.subTopicCount}>{subTopic.count}+ issues</span>
                                        </div>
                                    ))}
                                    {topic.subTopics.length > 4 && (
                                        <div style={{ ...styles.subTopicItem, borderBottom: "none" }}>
                                            <span style={{ color: topic.color, fontWeight: "600", fontSize: "13px" }}>
                                                +{topic.subTopics.length - 4} more issues
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Quick Solutions */}
            <div style={styles.quickHelpSection}>
                <h3 style={styles.sectionTitle}>
                    <FaCheckCircle /> Quick Solutions
                </h3>
                <div style={styles.quickSolutionsGrid}>
                    {quickSolutions.map(solution => (
                        <Link
                            key={solution.id}
                            to={solution.link}
                            style={styles.solutionCard}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-5px)";
                                e.currentTarget.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.15)";
                                e.currentTarget.style.borderColor = "#667eea";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                                e.currentTarget.style.borderColor = "#e8e8e8";
                            }}
                        >
                            <div style={styles.solutionIcon}>{solution.icon}</div>
                            <h4 style={styles.solutionTitle}>{solution.title}</h4>
                            <p style={styles.solutionDescription}>{solution.description}</p>
                            <span style={styles.solutionTime}>⏱️ {solution.time}</span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Recent Orders (Contextual Help) */}
            <div style={styles.recentOrdersSection}>
                <h3 style={styles.sectionTitle}>
                    <FaClock /> Recent Orders - Need Help?
                </h3>
                {recentOrders.map(order => (
                    <div key={order.id} style={styles.orderCard}>
                        <div style={styles.orderHeader}>
                            <span style={styles.orderId}>{order.id}</span>
                            <span style={getStatusStyle(order.status)}>{order.status}</span>
                        </div>
                        <div style={styles.orderDetails}>
                            <div style={styles.orderDetailItem}>
                                <strong>Restaurant:</strong> {order.restaurant}
                            </div>
                            <div style={styles.orderDetailItem}>
                                <strong>Amount:</strong> {order.amount}
                            </div>
                            <div style={styles.orderDetailItem}>
                                <strong>Time:</strong> {order.time}
                            </div>
                        </div>
                        {order.issues && order.issues.length > 0 && (
                            <div style={styles.orderIssues}>
                                {order.issues.map((issue, index) => (
                                    <span key={index} style={styles.issueTag}>{issue}</span>
                                ))}
                                <button
                                    style={{
                                        marginLeft: "auto",
                                        backgroundColor: "#667eea",
                                        color: "white",
                                        border: "none",
                                        padding: "8px 20px",
                                        borderRadius: "20px",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => window.location.href = `/support/order/${order.id}`}
                                >
                                    Get Help
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

       

           
        </div>
    );
};

export default HelpSupportCenter;